// MCP OAuth consent (/oauth/consent): when the user must sign in first, the
// consent route is kept in sessionStorage (it survives the Google sign-in
// redirect in the same tab) and the login returns to it instead of the app.

export const OAUTH_CONSENT_PATH = "/oauth/consent";
export const OAUTH_CONSENT_RETURN_STORAGE_KEY = "linkedscope_oauth_consent_return";
/** An abandoned connection attempt does not hijack a later sign-in in the same tab. */
export const OAUTH_CONSENT_RETURN_TTL_MS = 15 * 60 * 1000;

/** Only the consent route itself is accepted, so the stored value cannot send the user anywhere else. */
export const isConsentReturnRoute = (value) =>
  typeof value === "string" && value.startsWith(`${OAUTH_CONSENT_PATH}?`) && !value.includes("//");

export const savePendingConsentReturn = (route, storage = window.sessionStorage, now = Date.now()) => {
  if (!isConsentReturnRoute(route)) return;
  try {
    storage.setItem(OAUTH_CONSENT_RETURN_STORAGE_KEY, JSON.stringify({ route, savedAt: now }));
  } catch {
    // Without storage the user starts the connection again from the AI client.
  }
};

export const clearPendingConsentReturn = (storage = window.sessionStorage) => {
  try {
    storage.removeItem(OAUTH_CONSENT_RETURN_STORAGE_KEY);
  } catch {}
};

/** Reads and clears the pending consent route; null when there is none or it expired. */
export const takePendingConsentReturn = (storage = window.sessionStorage, now = Date.now()) => {
  let stored = null;
  try {
    stored = JSON.parse(storage.getItem(OAUTH_CONSENT_RETURN_STORAGE_KEY) || "null");
  } catch {
    stored = null;
  }
  clearPendingConsentReturn(storage);
  const fresh = Number(stored?.savedAt) > 0 && now - Number(stored.savedAt) <= OAUTH_CONSENT_RETURN_TTL_MS;
  return fresh && isConsentReturnRoute(stored.route) ? stored.route : null;
};

/** The authorization request as the API expects it, from the consent route query. */
export const readAuthorizationRequest = (search) => {
  const params = new URLSearchParams(search || "");
  const keys = [
    "client_id",
    "redirect_uri",
    "response_type",
    "state",
    "scope",
    "code_challenge",
    "code_challenge_method",
    "resource",
  ];
  const request = {};
  keys.forEach((key) => {
    const value = params.get(key);
    if (value !== null) request[key] = value;
  });
  return request;
};

/** Leaves the app for the AI app's redirect URI (a separate function so tests can observe it). */
export const sendBrowserTo = (url, { replace = false } = {}) => {
  if (replace) window.location.replace(url);
  else window.location.assign(url);
};
